import { RolePermission } from '@yandex-360/telemost-sdk';

import { NoiseCancellationType, VirtualBackgroundType } from '../../../../../common/types/settings';
import type { ScrollControllerPayload } from '../../../shared/features/scroll-controller';
import { SettingsSectionKey } from '../../../shared/features/settings-modal/constants';
import { AccessLevel, ReactionType } from '../../../types';
import { ConferenceStatus, GRID_VIEW } from '../constants';
import { PermissionsState } from '../features/permissions';
import { DisconnectPayloadError, DisconnectReason, OutgoingCallStatuses } from '../types';

export enum Errors {
    EXPIRED_LINK = 'EXPIRED_LINK',
    INVALID_LINK = 'INVALID_LINK',
    UNSUPPORTED_BROWSER = 'UNSUPPORTED_BROWSER',
    UNSUPPORTED_TOUCH = 'UNSUPPORTED_TOUCH',
    UNSUPPORTED_MESSENGER = 'UNSUPPORTED_MESSENGER',
    LIMIT_USERS_COUNT = 'LIMIT_USERS_COUNT',
    UNKNOWN = 'UNKNOWN',
}

export enum IncomingEvents {
    SET_MUTE = 'SET_MUTE',
    SET_GRID_ITEM_RATIO = 'SET_GRID_ITEM_RATIO',
    CANCEL_CALL = 'CANCEL_CALL',
    SHARE_SCREEN = 'SHARE_SCREEN',
    UNREAD_COUNT = 'UNREAD_COUNT',
    SET_MEDIA_DEVICE = 'SET_MEDIA_DEVICE',
    SET_CALLEE_PARTICIPANT = 'SET_CALLEE_PARTICIPANT',
    SET_CALL_STATUS = 'SET_CALL_STATUS',
    START_SCREEN_RECORDING_RESULT = 'START_SCREEN_RECORDING_RESULT',
    ADD_SCREEN_RECORDING_CHUNK_RESULT = 'ADD_SCREEN_RECORDING_CHUNK_RESULT',
    END_SCREEN_RECORDING_RESULT = 'END_SCREEN_RECORDING_RESULT',
    SET_IS_ONE_ON_ONE = 'SET_IS_ONE_ON_ONE',
    CLOSE_FULL_SCREEN = 'CLOSE_FULL_SCREEN',
    SET_GRID_VIEW = 'SET_GRID_VIEW',
    SET_REACTION = 'SET_REACTION',
    SET_PARTICIPANT_LIST_VISIBILITY = 'SET_PARTICIPANT_LIST_VISIBILITY',
    SET_CHAT_VISIBILITY = 'SET_CHAT_VISIBILITY',
    SCROLL_PARTICIPANTS = 'SCROLL_PARTICIPANTS',
    SET_GRID_PAGE = 'SET_GRID_PAGE',
    ACCEPT_CALL = 'ACCEPT_CALL',
    SET_NOISE_CANCELLATION = 'SET_NOISE_CANCELLATION',
    SET_VIRTUAL_BACKGROUND = 'SET_VIRTUAL_BACKGROUND',
    CREATE_BROADCAST = 'CREATE_BROADCAST',
    SET_CONFERENCE_PERMISSION = 'SET_CONFERENCE_PERMISSION',
    SET_SUMMARIZATION_MAIL_FOR_PARTICIPANTS = 'SET_SUMMARIZATION_FOR_MEMBERS_ALLOWED',
}

export enum OutgoingEvents {
    CONNECTED = 'CONNECTED',
    DISCONNECTED = 'DISCONNECTED',
    ERROR = 'ERROR',
    CHAT_OPEN = 'CHAT_OPEN',
    CLOSE_JOIN_SCREEN = 'CLOSE_JOIN_SCREEN',
    ADD_USERS_EVENT = 'ADD_USERS_EVENT',
    ERROR_PAGE_BUTTON_CLICK = 'ERROR_PAGE_BUTTON_CLICK',
    SHARE_SCREEN_BUTTON_CLICK = 'SHARE_SCREEN_BUTTON_CLICK',
    START_CALL_CLICK = 'START_CALL_CLICK',
    SET_MEDIA_DEVICE = 'SET_MEDIA_DEVICE',
    START_SCREEN_RECORDING = 'START_SCREEN_RECORDING',
    ADD_SCREEN_RECORDING_CHUNK = 'ADD_SCREEN_RECORDING_CHUNK',
    END_SCREEN_RECORDING = 'END_SCREEN_RECORDING',
    SET_FULL_SCREEN = 'SET_FULL_SCREEN',
    SET_SHARING = 'SET_SHARING',
    UI_STATE_CHANGED = 'UI_STATE_CHANGED',
    CONFERENCE_STATUS = 'CONFERENCE_STATUS',
    MEETING_INFO = 'MEETING_INFO',
    SET_MUTE = 'SET_MUTE',
    SET_NOISE_CANCELLATION = 'SET_NOISE_CANCELLATION',
    SET_VIRTUAL_BACKGROUND = 'SET_VIRTUAL_BACKGROUND',
    OPEN_SETTINGS = 'OPEN_SETTINGS',
    SET_CONFERENCE_PERMISSION_STATE = 'SET_CONFERENCE_PERMISSION_STATE',
    SET_SUMMARIZATION_MAIL_FOR_PARTICIPANTS = 'SET_SUMMARIZATION_MAIL_FOR_PARTICIPANTS',
}
export interface SyncMediaDevicePayload {
    deviceKind: MediaDeviceKind;
    commonDeviceId: string;
    doNotUpdateLocalStore: boolean;
}

type CreateBroadcastAccessLevel = AccessLevel.PUBLIC | AccessLevel.ORGANIZATION;

export interface SetMuteIncomingPayload {
    type: 'audio' | 'video';
    value: boolean;
}

export interface SetMuteOutgoingPayload {
    type: 'audio' | 'video';
    value: boolean;
}

interface SetMuteIncomingMessage {
    event: IncomingEvents.SET_MUTE;
    payload: SetMuteIncomingPayload;
}

export interface SetMuteOutgoingMessage {
    event: OutgoingEvents.SET_MUTE;
    payload: SetMuteOutgoingPayload;
}
interface SetGridItemRatioMessage {
    event: IncomingEvents.SET_GRID_ITEM_RATIO;
    payload: {
        value: 'square' | 'default';
    };
}

interface CancelCallMessage {
    event: IncomingEvents.CANCEL_CALL;
}

interface ShareScreenMessage {
    event: IncomingEvents.SHARE_SCREEN;
    payload: {
        value: string;
    };
}

interface UnreadCountMessage {
    event: IncomingEvents.UNREAD_COUNT;
    payload: {
        value: number;
    };
}

interface CloseFullScreen {
    event: IncomingEvents.CLOSE_FULL_SCREEN;
}

interface ConnectMessage {
    event: OutgoingEvents.CONNECTED;
    payload: {
        id: string;
    };
}

interface DisconnectMessage {
    event: OutgoingEvents.DISCONNECTED;
    payload: {
        id: string;
        error?: DisconnectPayloadError;
        source?: DisconnectReason;
    };
}

interface ErrorMessage {
    event: OutgoingEvents.ERROR;
    payload: {
        value: {
            code: string;
            message?: string;
        };
    };
}

interface ChatOpenMessage {
    event: OutgoingEvents.CHAT_OPEN;
}

interface ShareScreenButtonClickMessage {
    event: OutgoingEvents.SHARE_SCREEN_BUTTON_CLICK;
}

interface StartCallClickMessage {
    event: OutgoingEvents.START_CALL_CLICK;
}

interface CloseJoinScreenMessage {
    event: OutgoingEvents.CLOSE_JOIN_SCREEN;
    payload: {
        source: DisconnectReason;
    };
}

interface AddUsersMessage {
    event: OutgoingEvents.ADD_USERS_EVENT;
}

interface ErrorPageButtonClickMessage {
    event: OutgoingEvents.ERROR_PAGE_BUTTON_CLICK;
}

interface SetMediaDeviceIncomingMessage {
    event: OutgoingEvents.SET_MEDIA_DEVICE;
    payload: SyncMediaDevicePayload;
}

interface SetMediaDeviceOutgoingMessage {
    event: IncomingEvents.SET_MEDIA_DEVICE;
    payload: SyncMediaDevicePayload;
}

interface SetFullScreenOutgoingMessage {
    event: OutgoingEvents.SET_FULL_SCREEN;
    payload: {
        isFullScreenEnabled: boolean;
    };
}

interface SetSharingOutgoingMessage {
    event: OutgoingEvents.SET_SHARING;
    payload: {
        isSharing: boolean;
    };
}

export interface UiStateChangedPayload {
    videoEnabled?: boolean;
    audioEnabled?: boolean;
    reaction?: ReactionType | null;
    gridView?: GRID_VIEW;
    chatVisible?: boolean;
    participantsListVisible?: boolean;
    isChatLoading?: boolean;
    hasGridNextPage?: boolean;
    hasGridPrevPage?: boolean;
    canScrollParticipantsListUp?: boolean;
    canScrollParticipantsListDown?: boolean;
}

interface UiStateChangedMessage {
    event: OutgoingEvents.UI_STATE_CHANGED;
    payload: UiStateChangedPayload;
}

interface ConferenceStatusMessage {
    event: OutgoingEvents.CONFERENCE_STATUS;
    payload: {
        status: ConferenceStatus;
    };
}

interface MeetingInfoPayload {
    meetingName?: string;
    meetingId?: string;
}

interface MeetingInfoMessage {
    event: OutgoingEvents.MEETING_INFO;
    payload: MeetingInfoPayload;
}

type RequestMessage<T, P> = {
    event: T;
    reqId: string;
} & (P extends NonNullable<P>
    ? {
          payload: P;
      }
    : {
          payload?: P;
      });

type ResponseMessage<T, P, E = Error> = RequestMessage<T, P | undefined> & {
    event: T;
    error?: E;
};

type StartScreenRecordingResultMessage = ResponseMessage<IncomingEvents.START_SCREEN_RECORDING_RESULT, undefined>;

type AddScreenRecordingChunkResultMessage = ResponseMessage<
    IncomingEvents.ADD_SCREEN_RECORDING_CHUNK_RESULT,
    undefined
>;

type EndScreenRecordingResultMessage = ResponseMessage<IncomingEvents.END_SCREEN_RECORDING_RESULT, undefined>;

interface StartScreenRecordingPayload {
    startTime: number;
    mimeType: string;
}

type StartScreenRecordingMessage = RequestMessage<OutgoingEvents.START_SCREEN_RECORDING, StartScreenRecordingPayload>;

interface AddScreenRecordingChunkPayload {
    chunk: Blob;
    chunkDuration: number;
}

type AddScreenRecordingChunkMessage = RequestMessage<
    OutgoingEvents.ADD_SCREEN_RECORDING_CHUNK,
    AddScreenRecordingChunkPayload
>;

type EndScreenRecordingMessage = RequestMessage<OutgoingEvents.END_SCREEN_RECORDING, undefined>;

interface SetCallStatusMessage {
    event: IncomingEvents.SET_CALL_STATUS;
    payload: OutgoingCallStatuses;
}

interface SetCalleeParticipantPayload {
    displayName: string;
    avatarUrl: Maybe<string>;
}

interface SetCalleeParticipantMessage {
    event: IncomingEvents.SET_CALLEE_PARTICIPANT;
    payload: SetCalleeParticipantPayload;
}

interface SetIsOneOnOne {
    event: IncomingEvents.SET_IS_ONE_ON_ONE;
    payload: {
        value: boolean;
    };
}

interface SetGridView {
    event: IncomingEvents.SET_GRID_VIEW;
    payload: {
        gridView: GRID_VIEW;
    };
}

interface SetReaction {
    event: IncomingEvents.SET_REACTION;
    payload: {
        type: ReactionType;
        value: boolean;
    };
}

interface SetParticipantListVisibility {
    event: IncomingEvents.SET_PARTICIPANT_LIST_VISIBILITY;
    payload: {
        value: boolean;
    };
}

interface SetChatVisibility {
    event: IncomingEvents.SET_CHAT_VISIBILITY;
    payload: {
        value: boolean;
    };
}

interface SetGridPageMessage {
    event: IncomingEvents.SET_GRID_PAGE;
    payload: {
        direction: 'next' | 'prev';
    };
}

interface AcceptCall {
    event: IncomingEvents.ACCEPT_CALL;
}

interface CreateBroadcastMessage {
    event: IncomingEvents.CREATE_BROADCAST;
    payload: {
        caption?: string;
        description?: string;
        accessLevel?: CreateBroadcastAccessLevel;
        autoStart?: false;
    };
}

interface ScrollParticipantsMessage {
    event: IncomingEvents.SCROLL_PARTICIPANTS;
    payload: ScrollControllerPayload;
}

interface SetNoiseCancellationIncomingMessage {
    event: IncomingEvents.SET_NOISE_CANCELLATION;
    payload: {
        type: NoiseCancellationType;
    };
}

interface SetVirtualBackgroundIncomingMessage {
    event: IncomingEvents.SET_VIRTUAL_BACKGROUND;
    payload: {
        type: VirtualBackgroundType;
    };
}
interface SetNoiseCancellationOutgoingMessage {
    event: OutgoingEvents.SET_NOISE_CANCELLATION;
    payload: {
        type: NoiseCancellationType;
    };
}

interface SetVirtualBackgroundOutgoingMessage {
    event: OutgoingEvents.SET_VIRTUAL_BACKGROUND;
    payload: {
        type: VirtualBackgroundType;
    };
}

interface OpenSettingsOutgoingMessage {
    event: OutgoingEvents.OPEN_SETTINGS;
    payload: { section?: SettingsSectionKey };
}

interface SetConferencePermissionMessage {
    event: IncomingEvents.SET_CONFERENCE_PERMISSION;
    payload: {
        permissionName: RolePermission;
        value: boolean;
    };
}

interface SetConferencePermissionStateMessage {
    event: OutgoingEvents.SET_CONFERENCE_PERMISSION_STATE;
    payload: {
        permissionState: PermissionsState;
    };
}

interface SetSummarizationMailForParticipantsIncomingMessage {
    event: IncomingEvents.SET_SUMMARIZATION_MAIL_FOR_PARTICIPANTS;
    payload: {
        value: boolean;
    };
}

interface SetSummarizationMailForParticipantsOutgoingMessage {
    event: OutgoingEvents.SET_SUMMARIZATION_MAIL_FOR_PARTICIPANTS;
    payload: {
        value: boolean;
    };
}
export type IncomingPostMessage =
    | UnreadCountMessage
    | SetMuteIncomingMessage
    | CancelCallMessage
    | SetGridItemRatioMessage
    | ShareScreenMessage
    | SetMediaDeviceOutgoingMessage
    | SetCalleeParticipantMessage
    | SetCallStatusMessage
    | StartScreenRecordingResultMessage
    | AddScreenRecordingChunkResultMessage
    | EndScreenRecordingResultMessage
    | SetIsOneOnOne
    | CloseFullScreen
    | SetGridView
    | SetReaction
    | SetParticipantListVisibility
    | SetChatVisibility
    | ScrollParticipantsMessage
    | SetGridPageMessage
    | AcceptCall
    | CreateBroadcastMessage
    | SetNoiseCancellationIncomingMessage
    | SetVirtualBackgroundIncomingMessage
    | SetConferencePermissionMessage
    | SetSummarizationMailForParticipantsIncomingMessage;

export type OutgoingPostMessage =
    | ConnectMessage
    | DisconnectMessage
    | ErrorMessage
    | ChatOpenMessage
    | CloseJoinScreenMessage
    | AddUsersMessage
    | ErrorPageButtonClickMessage
    | ShareScreenButtonClickMessage
    | StartCallClickMessage
    | SetMediaDeviceIncomingMessage
    | StartScreenRecordingMessage
    | AddScreenRecordingChunkMessage
    | EndScreenRecordingMessage
    | SetFullScreenOutgoingMessage
    | SetSharingOutgoingMessage
    | UiStateChangedMessage
    | ConferenceStatusMessage
    | MeetingInfoMessage
    | SetMuteOutgoingMessage
    | SetNoiseCancellationOutgoingMessage
    | SetVirtualBackgroundOutgoingMessage
    | OpenSettingsOutgoingMessage
    | SetSummarizationMailForParticipantsOutgoingMessage
    | SetConferencePermissionStateMessage;
